import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'

import SelectInput from '@components/SelectInput'
import { ThemeEnum } from '@hooks/contexts/ThemeContext/interface'
import { ContrastBlackIcon } from '@components/icons/ContrastBlackIcon'
import { ContrastBlackLightIcon } from '@components/icons/ContrastBlackLightIcon'
import { DarkModeIcon } from '@components/icons/DarkModeIcon'
import { Font14Icon } from '@components/icons/Font14Icon'
import { Font16Icon } from '@components/icons/Font16Icon'
import { Font18Icon } from '@components/icons/Font18Icon'
import { Font20Icon } from '@components/icons/Font20Icon'
import { Font22Icon } from '@components/icons/Font22Icon'
import { Font24Icon } from '@components/icons/Font24Icon'
import { HalfMoonIcon } from '@components/icons/HalfMoonIcon'
import { LightIcon } from '@components/icons/LightIcon'
import { LineHeight125Icon } from '@components/icons/LineHeight125Icon'
import { LineHeight15Icon } from '@components/icons/LineHeight15Icon'
import { LineHeight25Icon } from '@components/icons/LineHeight25Icon'
import { LineHeight2Icon } from '@components/icons/LineHeight2Icon'
import { LineHeight3Icon } from '@components/icons/LineHeight3Icon'
import { ThemeButtonProps } from './interface'
import { Button } from './Button'

const fontFamilyOptions = [
  {
    value: 'notoSerif',
    label: 'Noto Serif',
  },
  {
    value: 'trirong',
    label: 'Trirong',
  },
  {
    value: 'sarabun',
    label: 'Sarabun',
  },
  {
    value: 'jsJindara',
    label: 'JS Jindara',
  },
  {
    value: 'baiJamjuree',
    label: 'Bai Jamjuree',
  },
]

export function ThemeButton({
  theme,
  fontSize,
  lineHeight,
  fontFamily,
  handleThemeChange,
  updateFontSize,
  updateLineHeight,
  updateFontFamily,
}: ThemeButtonProps) {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [visible, setVisible] = useState(false)

  function openDropdown() {
    if (buttonRef.current && dropdownRef.current) {
      const { width } = window.screen
      const { left } = buttonRef.current?.getBoundingClientRect()

      if (left + 290 > width) {
        dropdownRef.current.style.right = '0px'
        dropdownRef.current.style.left = ''
      } else {
        dropdownRef.current.style.left = `${Math.max(left - 145, 0)}px`
        dropdownRef.current.style.right = ''
      }
    }

    setVisible(prev => !prev)
  }

  function clickOutside(e: any) {
    if (
      dropdownRef.current &&
      buttonRef.current &&
      !dropdownRef.current.contains(e.target) &&
      !buttonRef.current.contains(e.target)
    ) {
      setVisible(false)
    }
  }

  useEffect(() => {
    window.addEventListener('click', clickOutside)

    return () => {
      window.removeEventListener('click', clickOutside)
    }
  }, [])

  return (
    <div className='text-secondary'>
      <button
        ref={buttonRef}
        type='button'
        className={cn(
          'flex justify-center items-center space-x-[5px] font-mitr font-medium w-[120px] h-[40px] rounded-[20px] mobile:w-[30px] mobile:h-[30px]',
          {
            'bg-gray-4': theme === ThemeEnum.LIGHT,
            'bg-white': theme === ThemeEnum.SEPIA,
            'bg-dark-9 text-dark-10': theme === ThemeEnum.NIGHT,
            'bg-dark-3 text-gray-16': theme === ThemeEnum.DARK,
          }
        )}
        onClick={openDropdown}
      >
        <ContrastBlackIcon width='24' height='24' />
        <span className='mobile:hidden'>ปรับแต่ง</span>
      </button>

      <div
        ref={dropdownRef}
        className={cn(
          'fixed p-[20px_30px_30px] text-secondary w-[290px] shadow border-[0.5px] rounded-[8px] top-[124px] mobile:top-[110px]',
          {
            hidden: !visible,
            'bg-white border-gray-4': theme === ThemeEnum.LIGHT,
            'bg-sepia border-sepia-2': theme === ThemeEnum.SEPIA,
            'bg-dark border-dark-3': theme === ThemeEnum.NIGHT,
            'bg-dark-5 border-dark-3': theme === ThemeEnum.DARK,
          }
        )}
      >
        <div
          className={cn('text-[14px] font-mitr text-left', {
            'text-white': theme === ThemeEnum.NIGHT,
            'text-gray-16': theme === ThemeEnum.DARK,
          })}
        >
          โหมดสว่าง/มืด
        </div>
        <div className='flex space-x-[20px] mt-[8px]'>
          <button
            type='button'
            className={cn('border rounded-[4px] w-[30px] h-[30px]', {
              'bg-white border-gray-3': theme !== ThemeEnum.LIGHT,
              'bg-white shadow-2 border-primary': theme === ThemeEnum.LIGHT,
              'bg-white opacity-60': [
                ThemeEnum.SEPIA,
                ThemeEnum.NIGHT,
              ].includes(theme),
              'bg-dark-4 opacity-60 text-gray-16 border-gray-16':
                theme === ThemeEnum.DARK,
            })}
            onClick={() => handleThemeChange(ThemeEnum.LIGHT)}
          >
            <LightIcon className='mx-auto' width='20' height='20' />
          </button>
          <button
            type='button'
            className={cn('border rounded-[4px] w-[30px] h-[30px]', {
              'border-gray-8 opacity-60 bg-sepia-3': theme === ThemeEnum.LIGHT,
              'border-primary bg-sepia-3': theme === ThemeEnum.SEPIA,
              'bg-dark-4 opacity-50 text-gray-16 border-gray-16':
                theme === ThemeEnum.DARK,
              'bg-sepia-4 opacity-60 text-gray-16 border-gray-16':
                theme === ThemeEnum.NIGHT,
            })}
            onClick={() => handleThemeChange(ThemeEnum.SEPIA)}
          >
            <ContrastBlackLightIcon
              className='mx-auto'
              width='20'
              height='20'
            />
          </button>
          <button
            type='button'
            className={cn('border rounded-[4px] w-[30px] h-[30px]', {
              'border-gray-3 opacity-60 bg-dark-2 text-white': [
                ThemeEnum.LIGHT,
                ThemeEnum.SEPIA,
              ].includes(theme),
              'bg-dark-2 border-primary text-white': theme === ThemeEnum.NIGHT,
              'bg-dark-4 opacity-60 border-gray-16 text-gray-16':
                theme === ThemeEnum.DARK,
            })}
            onClick={() => handleThemeChange(ThemeEnum.NIGHT)}
          >
            <HalfMoonIcon className='mx-auto' width='20' height='20' />
          </button>
          <button
            type='button'
            className={cn('border rounded-[4px] w-[30px] h-[30px]', {
              'text-white border-gray-3 opacity-60 bg-dark-7': [
                ThemeEnum.LIGHT,
                ThemeEnum.SEPIA,
                ThemeEnum.NIGHT,
              ].includes(theme),
              'text-primary border-primary bg-dark-4': theme === ThemeEnum.DARK,
            })}
            onClick={() => handleThemeChange(ThemeEnum.DARK)}
          >
            <DarkModeIcon className=' mx-auto' width='20' height='20' />
          </button>
        </div>
        <hr
          className={cn('my-[20px]', {
            'text-gray-3': ![ThemeEnum.NIGHT, ThemeEnum.DARK].includes(theme),
            'text-dark-3': [ThemeEnum.NIGHT, ThemeEnum.DARK].includes(theme),
          })}
        />
        <div
          className={cn('text-[14px] font-mitr text-left', {
            'text-white': theme === ThemeEnum.NIGHT,
            'text-gray-16': theme === ThemeEnum.DARK,
          })}
        >
          ฟอนต์
        </div>
        <SelectInput
          value={fontFamily}
          options={fontFamilyOptions}
          onChange={updateFontFamily}
        />
        <hr
          className={cn('my-[20px]', {
            'text-gray-3': ![ThemeEnum.NIGHT, ThemeEnum.DARK].includes(theme),
            'text-dark-3': [ThemeEnum.NIGHT, ThemeEnum.DARK].includes(theme),
          })}
        />
        <div
          className={cn('text-[14px] font-mitr text-left', {
            'text-white': theme === ThemeEnum.NIGHT,
            'text-gray-16': theme === ThemeEnum.DARK,
          })}
        >
          ขนาดตัวอักษร
        </div>
        <div className='flex space-x-[10px] mt-[8px]'>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={fontSize === 14}
            onClick={() => updateFontSize(14)}
          >
            <Font14Icon className='mx-auto' width='6.9' height='8' />
          </Button>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={fontSize === 16}
            onClick={() => updateFontSize(16)}
          >
            <Font16Icon className='mx-auto' width='8.8' height='10.3' />
          </Button>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={fontSize === 18}
            onClick={() => updateFontSize(18)}
          >
            <Font18Icon className='mx-auto' width='10.8' height='12.6' />
          </Button>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={fontSize === 20}
            onClick={() => updateFontSize(20)}
          >
            <Font20Icon className='mx-auto' width='11.8' height='13.7' />
          </Button>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={fontSize === 22}
            onClick={() => updateFontSize(22)}
          >
            <Font22Icon className='mx-auto' width='13.7' height='16' />
          </Button>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={fontSize === 24}
            onClick={() => updateFontSize(24)}
          >
            <Font24Icon className='mx-auto' width='15.5' height='18.2' />
          </Button>
        </div>
        <hr
          className={cn('my-[20px]', {
            'text-gray-3': ![ThemeEnum.NIGHT, ThemeEnum.DARK].includes(theme),
            'text-dark-3': [ThemeEnum.NIGHT, ThemeEnum.DARK].includes(theme),
          })}
        />
        <div
          className={cn('text-[14px] font-mitr text-left', {
            'text-white': theme === ThemeEnum.NIGHT,
            'text-gray-16': theme === ThemeEnum.DARK,
          })}
        >
          ระยะห่างระหว่างบรรทัด
        </div>
        <div className='flex space-x-[10px] mt-[8px]'>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={lineHeight === 1.25}
            onClick={() => updateLineHeight(1.25)}
          >
            <LineHeight125Icon className='mx-auto' width='20' height='10' />
          </Button>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={lineHeight === 1.5}
            onClick={() => updateLineHeight(1.5)}
          >
            <LineHeight15Icon className='mx-auto' width='20' height='12' />
          </Button>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={lineHeight === 2}
            onClick={() => updateLineHeight(2)}
          >
            <LineHeight2Icon className='mx-auto' width='20' height='14' />
          </Button>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={lineHeight === 2.5}
            onClick={() => updateLineHeight(2.5)}
          >
            <LineHeight25Icon className='mx-auto' width='20' height='18' />
          </Button>
          <Button
            isDarkTheme={theme === ThemeEnum.DARK}
            isActive={lineHeight === 3}
            onClick={() => updateLineHeight(3)}
          >
            <LineHeight3Icon className='mx-auto' width='20' height='20' />
          </Button>
        </div>
      </div>
    </div>
  )
}
